import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mic, Square, Save, Send, Settings, MessageSquare, FileText, Loader2, LogOut, CreditCard, Check, RefreshCw, HelpCircle, Edit2, Check as CheckIcon, X, Pause, Play, Trash2, ChevronDown, ChevronRight, Plus, Folder, Tag, MoreVertical, Shield, Coins } from "lucide-react";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

let stripePromise = null;
if (STRIPE_KEY) {
  stripePromise = loadStripe(STRIPE_KEY);
}

// Auth Context
function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return { user, loading, logout, refreshUser: checkAuth };
}

// Landing Page
function LandingPage() {
  const handleLogin = () => {
    const origin = window.location.origin;
    const redirectPath = '/dashboard';
    const redirectUrl = encodeURIComponent(origin + redirectPath);
    const authBaseUrl = 'https://auth.emergentagent.com';
    const finalUrl = authBaseUrl + '/' + '?redirect=' + redirectUrl;
    window.location.replace(finalUrl);
  };

  return (
    <div style={{
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      minHeight: '100vh',
      color: '#fff',
      overflowY: 'auto',
      overflowX: 'hidden'
    }}>
      {/* Fixed Top Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{fontSize: '1.5rem', fontWeight: '700', color: '#0ea5e9'}}>
          DUMPHUB.AI <span style={{fontSize: '0.7rem', color: '#fbbf24', marginLeft: '0.5rem'}}>BETA</span>
        </div>
        <Button
          onClick={handleLogin}
          style={{
            background: '#0ea5e9',
            color: '#fff',
            padding: '0.75rem 2rem',
            fontSize: '1rem',
            fontWeight: '600',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(14, 165, 233, 0.4)',
            transition: 'all 0.3s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#0284c7';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(14, 165, 233, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#0ea5e9';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 14px rgba(14, 165, 233, 0.4)';
          }}
        >
          Sign in with Google →
        </Button>
      </nav>

      {/* Hero Section */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '8rem 2rem 4rem',
        textAlign: 'center'
      }}>
        <h1 style={{
          fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
          fontWeight: '800',
          marginBottom: '1.5rem',
          lineHeight: '1.1',
          background: 'linear-gradient(135deg, #fff 0%, #0ea5e9 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Speak Your Thoughts.<br />AI Does The Rest.
        </h1>
        
        <p style={{
          fontSize: '1.25rem',
          color: '#94a3b8',
          marginBottom: '2.5rem',
          maxWidth: '700px',
          margin: '0 auto 2.5rem',
          lineHeight: '1.6'
        }}>
          Revolutionary chunking system handles unlimited recording lengths without crashes. 7× faster than typing. Perfect for long meetings, brainstorms, and deep work sessions.
        </p>

        <div style={{display: 'flex', gap: '1rem', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap'}}>
          <Button
            onClick={handleLogin}
            style={{
              background: '#0ea5e9',
              color: '#fff',
              padding: '1.25rem 3rem',
              fontSize: '1.125rem',
              fontWeight: '700',
              border: 'none',
              borderRadius: '0.75rem',
              cursor: 'pointer',
              boxShadow: '0 10px 40px rgba(14, 165, 233, 0.4)',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#0284c7';
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 15px 50px rgba(14, 165, 233, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#0ea5e9';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 40px rgba(14, 165, 233, 0.4)';
            }}
          >
            Start Free - Sign in with Google
          </Button>
          <span style={{color: '#64748b', fontSize: '0.875rem'}}>⚠️ Beta - Use at your own risk</span>
        </div>

        {/* Quick Features */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '2rem',
          marginTop: '5rem',
          textAlign: 'left'
        }}>
          <div style={{padding: '2rem', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '1rem', border: '1px solid rgba(148, 163, 184, 0.1)'}}>
            <Mic style={{color: '#0ea5e9', width: '2.5rem', height: '2.5rem', marginBottom: '1rem'}} />
            <h3 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem'}}>Unlimited Voice DUMP</h3>
            <p style={{color: '#94a3b8'}}>Smart chunking handles hours of recording without browser crashes. Record as long as you need.</p>
          </div>
          
          <div style={{padding: '2rem', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '1rem', border: '1px solid rgba(148, 163, 184, 0.1)'}}>
            <MessageSquare style={{color: '#0ea5e9', width: '2.5rem', height: '2.5rem', marginBottom: '1rem'}} />
            <h3 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem'}}>AI Research</h3>
            <p style={{color: '#94a3b8'}}>Chat with AI that searches the web in real-time.</p>
          </div>
          
          <div style={{padding: '2rem', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '1rem', border: '1px solid rgba(148, 163, 184, 0.1)'}}>
            <FileText style={{color: '#0ea5e9', width: '2.5rem', height: '2.5rem', marginBottom: '1rem'}} />
            <h3 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem'}}>Web Crawling</h3>
            <p style={{color: '#94a3b8'}}>Paste URLs. AI extracts and analyzes content.</p>
          </div>
        </div>

        {/* CTA Section */}
        <div style={{
          marginTop: '5rem',
          padding: '3rem',
          background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(30, 41, 59, 0.5) 100%)',
          borderRadius: '1.5rem',
          border: '2px solid rgba(14, 165, 233, 0.3)'
        }}>
          <h2 style={{fontSize: '2rem', fontWeight: '700', marginBottom: '1rem'}}>Record Hours Without Limits</h2>
          <p style={{color: '#94a3b8', fontSize: '1.125rem', marginBottom: '2rem'}}>Our revolutionary chunking system solves the browser crash problem. Record entire meetings, brainstorms, and deep work sessions seamlessly.</p>
          <Button
            onClick={handleLogin}
            style={{
              background: '#0ea5e9',
              color: '#fff',
              padding: '1.25rem 3rem',
              fontSize: '1.125rem',
              fontWeight: '700',
              border: 'none',
              borderRadius: '0.75rem',
              cursor: 'pointer',
              boxShadow: '0 10px 40px rgba(14, 165, 233, 0.4)'
            }}
          >
            Sign in with Google - It's Free
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        textAlign: 'center',
        padding: '2rem',
        color: '#64748b',
        borderTop: '1px solid rgba(148, 163, 184, 0.1)',
        marginTop: '4rem'
      }}>
        <p>© 2025 DUMPHUB.AI - Beta Version</p>
      </footer>
    </div>
  );
}

// Dashboard Component
function Dashboard() {
  const { user, logout, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState("chat");
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [userApiKey, setUserApiKey] = useState("");
  const [isSavingKey, setIsSavingKey] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userSettings, setUserSettings] = useState(null);
  const [showIntentionPrompt, setShowIntentionPrompt] = useState(true);
  const [showElaborationPrompt, setShowElaborationPrompt] = useState(false);
  const [elaborationIdeas, setElaborationIdeas] = useState([]);
  const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingSessionTitle, setEditingSessionTitle] = useState("");
  const [autoSendAfterTranscribe, setAutoSendAfterTranscribe] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [collapsedProjects, setCollapsedProjects] = useState({});
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const [editingTags, setEditingTags] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [imageTypes, setImageTypes] = useState([]);
  const [selectedImageType, setSelectedImageType] = useState(null);
  const [imageStyle, setImageStyle] = useState("");
  const [customImagePrompt, setCustomImagePrompt] = useState("");
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [lastRecordingBlob, setLastRecordingBlob] = useState(null);
  const [lastRecordingFilename, setLastRecordingFilename] = useState(null);
  
  const chatEndRef = useRef(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (user) {
      loadUserApiKey();
      loadSessions();
      loadProjects();
    }
  }, [user]);

  useEffect(() => {
    // Refresh user data when mounting to ensure we have latest
    if (user && activeTab === "chat") {
      refreshUser();
    }
  }, [activeTab]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  const loadUserApiKey = async () => {
    try {
      const response = await axios.get(`${API}/user/api-key`, { withCredentials: true });
      if (response.data.openai_key) {
        setUserApiKey(response.data.openai_key);
      }
    } catch (error) {
      console.error("Error loading API key:", error);
    }
  };

  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      const response = await axios.get(`${API}/sessions`, { withCredentials: true });
      setSessions(response.data || []);
    } catch (error) {
      console.error("Error loading sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(response.data || []);
    } catch (error) {
      console.error("Error loading projects:", error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const createProject = async () => {
    if (!newProjectName.trim()) {
      toast.error("Project name cannot be empty");
      return;
    }

    try {
      await axios.post(`${API}/projects`, {
        name: newProjectName,
        color: "#3b82f6"
      }, { withCredentials: true });
      
      setNewProjectName("");
      setShowNewProjectDialog(false);
      await loadProjects();
      toast.success("Project created");
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error("Failed to create project");
    }
  };

  const deleteProject = async (projectId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (!window.confirm("Delete this project? Chats will be moved to Uncategorized.")) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
      await loadProjects();
      await loadSessions();
      toast.success("Project deleted");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  };

  const moveSessionToProject = async (sessionId, projectId) => {
    try {
      await axios.put(`${API}/sessions/${sessionId}/project`, {
        project_id: projectId
      }, { withCredentials: true });
      
      await loadSessions();
      toast.success("Chat moved to project");
    } catch (error) {
      console.error("Error moving session:", error);
      toast.error("Failed to move chat");
    }
  };

  const updateSessionTags = async (sessionId, tags) => {
    try {
      await axios.put(`${API}/sessions/${sessionId}/tags`, {
        tags: tags
      }, { withCredentials: true });
      
      await loadSessions();
      toast.success("Tags updated");
    } catch (error) {
      console.error("Error updating tags:", error);
      toast.error("Failed to update tags");
    }
  };

  const toggleProject = (projectId) => {
    setCollapsedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const saveUserApiKey = async () => {
    if (!userApiKey || userApiKey.trim() === "") {
      toast.error("Please enter a valid API key");
      return;
    }

    setIsSavingKey(true);
    try {
      await axios.post(`${API}/user/api-key`, 
        { openai_key: userApiKey },
        { withCredentials: true }
      );
      toast.success("API key saved successfully!");
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save API key");
    } finally {
      setIsSavingKey(false);
    }
  };

  const deleteUserApiKey = async () => {
    try {
      await axios.delete(`${API}/user/api-key`, { withCredentials: true });
      setUserApiKey("");
      toast.success("API key deleted");
      await refreshUser();
    } catch (error) {
      toast.error("Failed to delete API key");
    }
  };

  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (!isRecording) {
        setRecordingTime(0);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording, isPaused]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setTranscript("");
      toast.success("Recording started");
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Failed to start recording. Please allow microphone access.");
    }
  };

  const pauseRecording = () => {
    console.log("Pause clicked", { mediaRecorder: !!mediaRecorderRef.current, isRecording, isPaused });
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      toast.info("Recording paused");
    } else {
      console.log("Cannot pause - conditions not met");
    }
  };

  const resumeRecording = () => {
    console.log("Resume clicked", { mediaRecorder: !!mediaRecorderRef.current, isRecording, isPaused });
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      toast.success("Recording resumed");
    } else {
      console.log("Cannot resume - conditions not met");
    }
  };

  const stopRecording = () => {
    return new Promise((resolve) => {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.onstop = () => {
          mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
          resolve();
        };
        
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        setIsPaused(false);
        toast.success("Recording stopped");
      } else {
        resolve();
      }
    });
  };

  const stopAndTranscribe = async () => {
    console.log("Stop and transcribe clicked");
    console.log("Audio chunks before stop:", audioChunksRef.current.length);
    await stopRecording();
    console.log("Audio chunks after stop:", audioChunksRef.current.length);
    // Wait a moment for audio chunks to be processed
    setTimeout(() => {
      console.log("Calling transcribeAudio");
      transcribeAudio();
    }, 100);
  };

  const transcribeAudio = async () => {
    if (audioChunksRef.current.length === 0) {
      toast.error("No audio to transcribe");
      return;
    }

    setIsTranscribing(true);
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
      const filename = `dumphub-recording-${timestamp}.webm`;
      
      // Save the recording blob and filename for download/share
      setLastRecordingBlob(audioBlob);
      setLastRecordingFilename(filename);
      
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      if (currentSessionId) {
        formData.append('session_id', currentSessionId);
      }

      toast.info("Transcribing audio with Whisper...");
      
      const response = await axios.post(`${API}/transcribe/audio`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      setTranscript(response.data.transcript);
      setCurrentSessionId(response.data.session_id);
      
      console.log('=== RECORDING DEBUG ===');
      console.log('Recording blob saved:', lastRecordingBlob ? 'YES' : 'NO');
      console.log('Recording filename:', lastRecordingFilename);
      console.log('Blob size:', audioBlob.size);
      
      toast.success(`Transcription complete! Used ${response.data.tokens_used} tokens`);
      await refreshUser();
      await loadSessions(); // Refresh sessions list
    } catch (error) {
      console.error("Error transcribing audio:", error);
      toast.error(error.response?.data?.detail || "Failed to transcribe audio");
      setAutoSendAfterTranscribe(false);
    } finally {
      setIsTranscribing(false);
    }
  };

  const generateElaborationIdeas = async () => {
    // Get the last AI response
    const lastAiMessage = chatMessages.filter(m => m.role === 'assistant').pop();
    
    if (!lastAiMessage) {
      toast.error("No AI response to elaborate on yet. Send a message first!");
      return;
    }

    if (user.subscription_tier === 'free') {
      toast.error("Upgrade to a paid plan to use AI elaboration features");
      return;
    }

    setIsGeneratingIdeas(true);
    
    try {
      const prompt = `You are a creativity coach analyzing this AI response:

"${lastAiMessage.content}"

Act as a creativity coach that understands left-brain (logical, analytical, organized) vs right-brain (creative, intuitive, free-flowing) thinking styles.

Provide exactly 5 diverse elaboration suggestions that help the user expand or transform this response. 

**REQUIRED: One suggestion MUST ALWAYS be about creating meeting minutes/notes** (e.g., "Create formal meeting minutes from this discussion" or "Turn this into structured meeting notes with action items")

The other 4 suggestions should consider:

LEFT-BRAIN ELABORATIONS (if response is creative/abstract):
- Convert to bullet points or structured lists
- Add data, metrics, or specific examples
- Create step-by-step action plans
- Organize into frameworks or systems
- Add timelines or prioritization

RIGHT-BRAIN ELABORATIONS (if response is logical/structured):
- Explore creative analogies or metaphors
- Generate alternative perspectives
- Add storytelling or narrative elements
- Consider emotional or human aspects
- Brainstorm unconventional approaches

BALANCE ELABORATIONS:
- Deep dive into specific aspects
- Challenge assumptions
- Apply to real-world scenarios
- Connect to broader context
- Ask "what if" questions

Format: Return 5 concise, actionable follow-up prompts (1-2 sentences each). Mix left-brain and right-brain approaches based on what would add the most value. Make sure one is always about meeting minutes/notes.

Respond with just the 5 numbered suggestions, nothing else.`;

      const response = await axios.post(`${API}/chat/send`, {
        message: prompt,
        session_id: null // Don't save this to a session
      }, { withCredentials: true });

      // Parse the response to extract the 5 suggestions
      const suggestions = response.data.response
        .split('\n')
        .filter(line => line.trim().match(/^\d+[.)]/))
        .map(line => line.replace(/^\d+[.)]/, '').trim())
        .filter(s => s.length > 0);

      if (suggestions.length > 0) {
        setElaborationIdeas(suggestions.slice(0, 5));
        setShowElaborationPrompt(true);
        toast.success("Elaboration ideas generated!");
      } else {
        toast.error("Could not generate elaboration ideas. Please try again.");
      }
      
      await refreshUser(); // Update token count
    } catch (error) {
      console.error("Error generating ideas:", error);
      toast.error(error.response?.data?.detail || "Failed to generate elaboration ideas");
    } finally {
      setIsGeneratingIdeas(false);
    }
  };

  const useElaborationIdea = (idea) => {
    setChatInput(prevInput => {
      // If there's existing input, append the idea with a separator
      if (prevInput.trim()) {
        return `${prevInput}\n\n${idea}`;
      }
      return idea;
    });
    setShowElaborationPrompt(false);
  };


  const openImageDialog = async () => {
    const lastAiMessage = chatMessages.filter(m => m.role === 'assistant').pop();
    
    if (!lastAiMessage) {
      toast.error("No AI response to create an image from yet. Send a message first!");
      return;
    }

    if (user.subscription_tier === 'free') {
      toast.error("Upgrade to a paid plan to use AI image generation");
      return;
    }

    // Generate recommended image types based on last response
    const recommendedTypes = [
      { id: 'facebook_post', name: 'Facebook Post', description: '1200x630px, eye-catching and shareable' },
      { id: 'linkedin_post', name: 'LinkedIn Post', description: '1200x627px, professional and corporate' },
      { id: 'instagram_post', name: 'Instagram Post', description: '1080x1080px, vibrant and engaging' },
      { id: 'pictograph', name: 'Pictograph/Infographic', description: 'Simple, clean visual hierarchy' },
      { id: 'custom', name: 'Custom Prompt', description: 'Enter your own image generation prompt' }
    ];
    
    setImageTypes(recommendedTypes);
    setShowImageDialog(true);
    setSelectedImageType(null);
    setImageStyle('');
    setCustomImagePrompt('');
    setGeneratedImage(null);
  };

  const generateImage = async () => {
    if (!selectedImageType && !customImagePrompt.trim()) {
      toast.error("Please select an image type or enter a custom prompt");
      return;
    }

    console.log("Starting image generation...");
    console.log("Selected type:", selectedImageType);
    console.log("Style:", imageStyle);
    console.log("Custom prompt:", customImagePrompt);

    setGeneratingImage(true);
    
    try {
      const lastAiMessage = chatMessages.filter(m => m.role === 'assistant').pop();
      
      let prompt = '';
      if (selectedImageType && selectedImageType !== 'custom') {
        // Use AI response context + type
        prompt = `Based on this content: "${lastAiMessage.content.substring(0, 500)}..." Create a visual representation.`;
      } else {
        // Use custom prompt
        prompt = customImagePrompt;
      }

      console.log("Final prompt:", prompt);
      console.log("Making API call to:", `${API}/generate-image`);

      const response = await axios.post(`${API}/generate-image`, {
        prompt: prompt,
        image_type: selectedImageType !== 'custom' ? selectedImageType : null,
        style: imageStyle || null,
        session_id: currentSessionId
      }, { withCredentials: true });

      console.log("Image generated successfully!", response.data);
      setGeneratedImage(response.data.image_base64);
      toast.success("Image generated successfully!");
      await refreshUser(); // Update token count
      
    } catch (error) {
      console.error("Error generating image:", error);
      console.error("Error details:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to generate image");
    } finally {
      setGeneratingImage(false);
    }
  };

  const downloadImage = () => {
    if (!generatedImage) {
      toast.error("No image to download");
      return;
    }
    
    try {
      console.log("Starting download...");
      console.log("Generated image length:", generatedImage.length);
      
      // Convert base64 to blob for better browser compatibility
      const byteCharacters = atob(generatedImage);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      
      // Create download URL
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dumphub-image-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      toast.success("Image downloaded!");
      console.log("Download completed successfully!");
      
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download image: " + error.message);
    }
  };


  const downloadRecording = () => {
    if (!lastRecordingBlob) {
      toast.error("No recording available to download");
      return;
    }

    try {
      const audioUrl = URL.createObjectURL(lastRecordingBlob);
      const a = document.createElement('a');
      a.href = audioUrl;
      a.download = lastRecordingFilename || `dumphub-recording-${Date.now()}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(audioUrl);
      toast.success("Recording downloaded!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download recording");
    }
  };

  const shareRecording = async () => {
    if (!lastRecordingBlob) {
      toast.error("No recording available to share");
      return;
    }

    try {
      // Check if Web Share API is supported
      if (!navigator.share) {
        toast.error("Sharing is not supported in this browser. Use the download button instead.");
        return;
      }

      // Create a File object from the blob
      const file = new File(
        [lastRecordingBlob], 
        lastRecordingFilename || `dumphub-recording-${Date.now()}.webm`,
        { type: 'audio/webm' }
      );

      // Check if we can share files
      if (navigator.canShare && !navigator.canShare({ files: [file] })) {
        toast.error("Your browser doesn't support sharing audio files. Use the download button instead.");
        return;
      }

      // Share the file
      await navigator.share({
        files: [file],
        title: 'DUMPHUB Recording',
        text: 'Audio recording from DUMPHUB.AI'
      });

      toast.success("Recording shared!");
    } catch (error) {
      // User cancelled or sharing failed
      if (error.name === 'AbortError') {
        // User cancelled - don't show error
        console.log("Share cancelled by user");
      } else {
        console.error("Share error:", error);
        toast.error("Failed to share. Try downloading instead.");
      }
    }
  };

  const saveMP3 = async () => {
    if (audioChunksRef.current.length === 0) {
      toast.error("No audio to save");
      return;
    }

    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    const audioUrl = URL.createObjectURL(audioBlob);
    const a = document.createElement('a');
    a.href = audioUrl;
    a.download = `recording-${Date.now()}.webm`;
    a.click();
    toast.success("Audio file downloaded");
  };

  const sendToAI = async () => {
    if (!transcript) {
      toast.error("No transcript to send");
      return;
    }

    if (user.subscription_tier === 'free') {
      toast.error("Upgrade to a paid plan to use AI chat features");
      return;
    }

    setIsSending(true);
    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: transcript,
        session_id: currentSessionId
      }, { withCredentials: true });

      setCurrentSessionId(response.data.session_id);
      
      setChatMessages(prev => [
        ...prev,
        { role: "user", content: transcript },
        { role: "assistant", content: response.data.response }
      ]);

      // Clear transcript after sending
      setTranscript("");
      audioChunksRef.current = [];
      
      setActiveTab("chat");
      toast.success(`Message sent! Used ${response.data.tokens_used} tokens`);
      await refreshUser();
      await loadSessions(); // Refresh to show auto-generated title
      setAutoSendAfterTranscribe(false); // Reset flag
    } catch (error) {
      console.error("Error sending to AI:", error);
      toast.error(error.response?.data?.detail || "Failed to send message");
      setAutoSendAfterTranscribe(false); // Reset flag on error too
    } finally {
      setIsSending(false);
    }
  };

  const loadChatHistory = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/chat/history/${sessionId}`, { withCredentials: true });
      setChatMessages(response.data || []);
      setCurrentSessionId(sessionId);
      setActiveTab("chat");
    } catch (error) {
      console.error("Error loading chat history:", error);
      toast.error("Failed to load chat history");
    }
  };

  const startEditingSession = (session, e) => {
    e.stopPropagation(); // Prevent loading the session
    setEditingSessionId(session.id);
    setEditingSessionTitle(session.title);
  };

  const cancelEditingSession = (e) => {
    e.stopPropagation();
    setEditingSessionId(null);
    setEditingSessionTitle("");
  };

  const saveSessionTitle = async (sessionId, e) => {
    e.stopPropagation();
    if (!editingSessionTitle.trim()) {
      toast.error("Session title cannot be empty");
      return;
    }

    try {
      await axios.put(
        `${API}/sessions/${sessionId}`,
        { title: editingSessionTitle },
        { withCredentials: true }
      );
      
      // Update local sessions list
      setSessions(sessions.map(s => 
        s.id === sessionId ? { ...s, title: editingSessionTitle } : s
      ));
      
      setEditingSessionId(null);
      setEditingSessionTitle("");
      toast.success("Session renamed");
    } catch (error) {
      console.error("Error renaming session:", error);
      toast.error("Failed to rename session");
    }
  };

  const deleteSession = async (sessionId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    // Confirm deletion
    if (!window.confirm("Are you sure you want to delete this chat? This action cannot be undone.")) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/sessions/${sessionId}`, { withCredentials: true });
      
      // Remove from local sessions list
      setSessions(prevSessions => prevSessions.filter(s => s.id !== sessionId));
      
      // If we deleted the current session, clear the chat
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setChatMessages([]);
      }
      
      toast.success("Chat deleted successfully!");
    } catch (error) {
      console.error("Error deleting session:", error);
      toast.error(error.response?.data?.detail || "Failed to delete chat");
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) {
      toast.error("Please enter a message");
      return;
    }

    if (user.subscription_tier === 'free') {
      toast.error("Upgrade to a paid plan to use AI chat features");
      return;
    }

    setIsSending(true);
    const messageToSend = chatInput;
    setChatInput(""); // Clear input immediately for better UX

    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: messageToSend,
        session_id: currentSessionId
      }, { withCredentials: true });

      setCurrentSessionId(response.data.session_id);
      
      setChatMessages(prev => [
        ...prev,
        { role: "user", content: messageToSend },
        { role: "assistant", content: response.data.response }
      ]);

      toast.success(`Response received! Used ${response.data.tokens_used} tokens`);
      await refreshUser();
      await loadSessions(); // Refresh sessions list
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(error.response?.data?.detail || "Failed to send message");
      setChatInput(messageToSend); // Restore message on error
    } finally {
      setIsSending(false);
    }
  };


  const startNewChat = () => {
    setCurrentSessionId(null);
    setChatMessages([]);
    toast.info("Started new conversation");
  };

  const handleUpgrade = async (plan) => {
    try {
      const response = await axios.post(`${API}/subscription/create-checkout`, 
        { plan },
        { withCredentials: true }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Failed to create checkout session");
    }
  };

  const handleBuyTokens = async () => {
    try {
      const response = await axios.post(`${API}/subscription/buy-tokens`, {}, 
        { withCredentials: true }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to purchase tokens");
    }
  };

  const availableTokens = user ? 
    user.tokens_limit + (user.token_purchases * 1000000) - user.tokens_used : 0;
  const tokenPercentage = user && user.tokens_limit > 0 ? 
    (user.tokens_used / (user.tokens_limit + (user.token_purchases * 1000000))) * 100 : 0;

  if (!user) {
    return (
      <div className="loading-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Fixed Header */}
      <header className="app-header">
        <div className="app-header-left">
          <div className="app-logo">
            DUMPHUB.AI
          </div>
          <nav className="app-nav">
            <button onClick={() => setActiveTab('chat')} style={{fontWeight: activeTab === 'chat' ? '600' : '400', color: activeTab === 'chat' ? '#0891b2' : '#6b7280'}}>
              <MessageSquare className="w-4 h-4" style={{display: 'inline', marginRight: '0.5rem'}} />
              Chat
            </button>
            {user?.is_admin && (
              <button onClick={() => setActiveTab('admin')} style={{fontWeight: activeTab === 'admin' ? '600' : '400', color: activeTab === 'admin' ? '#0891b2' : '#6b7280'}}>
                <Shield className="w-4 h-4" style={{display: 'inline', marginRight: '0.5rem'}} />
                Admin
              </button>
            )}
          </nav>
        </div>
        <div className="app-header-right">
          {user?.subscription_tier !== 'free' && (
            <div className="token-display" style={{display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb'}}>
              <Coins className="w-5 h-5" style={{color: '#f59e0b'}} />
              <span style={{fontWeight: '600', fontSize: '0.95rem', color: '#111827'}}>
                {availableTokens.toLocaleString()}
              </span>
            </div>
          )}
          <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
            <button 
              onClick={() => setActiveTab('settings')}
              style={{background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem', borderRadius: '0.375rem', display: 'flex', alignItems: 'center', transition: 'background 0.2s'}}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              title="Settings & Subscription"
            >
              <Settings className="w-5 h-5" style={{color: '#6b7280'}} />
            </button>
            
            <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  style={{width: '32px', height: '32px', borderRadius: '50%', border: '2px solid #e5e7eb'}}
                />
              ) : (
                <div style={{width: '32px', height: '32px', borderRadius: '50%', background: '#0891b2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', fontSize: '0.875rem'}}>
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <span style={{fontSize: '0.875rem', color: '#111827', fontWeight: '500'}}>
                {user?.name}
              </span>
            </div>
            
            <Button variant="outline" onClick={logout} size="sm">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content Area - Show based on activeTab */}
      <div style={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} style={{display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden'}}>
        <TabsList style={{display: 'none'}}>
          <TabsTrigger value="chat"></TabsTrigger>
          <TabsTrigger value="subscription"></TabsTrigger>
          <TabsTrigger value="settings"></TabsTrigger>
        </TabsList>

        <TabsContent value="record" className="tab-content">
          <Card className="record-card">
            <CardHeader>
              <CardTitle>Brain DUMP / Dictate</CardTitle>
              <CardDescription>
                {user?.subscription_tier === 'free' 
                  ? `Free tier: ${user.transcriptions_today}/10 transcriptions today`
                  : 'Unlimited recordings with your subscription'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="record-content">
              {showIntentionPrompt && !isRecording && !transcript && (
                <div className="ideas-framework-prompt">
                  <h3>💡 DUMPHUB Pre-Recording</h3>
                  <p><strong>Set Your Intention Before You Start</strong></p>
                  <p>Take a moment to think about:</p>
                  <ul>
                    <li>What do you want to accomplish with this brain dump?</li>
                    <li>What's your main goal or challenge?</li>
                    <li>What ideas or research are you trying to capture?</li>
                  </ul>
                  <p className="tip-text">When you're ready, click DUMP to start capturing your thoughts!</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowIntentionPrompt(false)}
                    className="dismiss-btn"
                  >
                    Got it, let's go!
                  </Button>
                </div>
              )}

              <div className="record-button-container">
                {!isRecording ? (
                  <Button
                    data-testid="start-recording-btn"
                    onClick={startRecording}
                    className="record-btn start-btn dump-btn"
                    size="lg"
                  >
                    <Mic className="w-8 h-8" />
                    <span className="btn-label">DUMP</span>
                  </Button>
                ) : (
                  <Button
                    data-testid="stop-recording-btn"
                    onClick={stopRecording}
                    className="record-btn stop-btn"
                    size="lg"
                  >
                    <Square className="w-8 h-8" />
                  </Button>
                )}
              </div>

              {isRecording && (
                <div className="recording-indicator">
                  <span className="pulse"></span>
                  <span>Recording your brain dump... {formatTime(recordingTime)}</span>
                </div>
              )}

              {!isRecording && audioChunksRef.current.length > 0 && !transcript && (
                <div className="transcribe-section">
                  <Button 
                    data-testid="transcribe-btn"
                    onClick={transcribeAudio}
                    disabled={isTranscribing}
                    className="transcribe-btn"
                    size="lg"
                  >
                    {isTranscribing ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Transcribing your dump...
                      </>
                    ) : (
                      <>
                        <FileText className="w-5 h-5 mr-2" />
                        Transcribe with Whisper
                      </>
                    )}
                  </Button>
                </div>
              )}

              {transcript && (
                <>
                  <div className="transcript-container">
                    <Label>Your Brain Dump:</Label>
                    <div className="transcript-box" data-testid="transcript-display">
                      {transcript}
                    </div>
                    
                    {/* Download and Share Recording Buttons - ALWAYS SHOW FOR TESTING */}
                    <div style={{
                      display: 'flex',
                      gap: '1rem',
                      marginTop: '1.5rem',
                      flexWrap: 'wrap',
                      padding: '1rem',
                      background: '#1e293b',
                      borderRadius: '0.5rem',
                      border: '2px solid #0ea5e9'
                    }}>
                      <div style={{width: '100%', marginBottom: '0.5rem', color: '#94a3b8', fontSize: '0.875rem'}}>
                        💾 Recording Actions (Blob: {lastRecordingBlob ? 'Available ✅' : 'Not Available ❌'})
                      </div>
                      <Button
                        onClick={downloadRecording}
                        disabled={!lastRecordingBlob}
                        style={{
                          flex: 1,
                          minWidth: '160px',
                          height: '56px',
                          background: lastRecordingBlob ? '#0ea5e9' : '#475569',
                          color: 'white',
                          border: 'none',
                          fontSize: '1rem',
                          fontWeight: '600'
                        }}
                      >
                        <Save className="w-5 h-5 mr-2" />
                        Download Recording
                      </Button>
                      <Button
                        onClick={shareRecording}
                        disabled={!lastRecordingBlob}
                        style={{
                          flex: 1,
                          minWidth: '160px',
                          height: '56px',
                          background: lastRecordingBlob ? '#10b981' : '#475569',
                          color: 'white',
                          border: 'none',
                          fontSize: '1rem',
                          fontWeight: '600'
                        }}
                      >
                        <FileText className="w-5 h-5 mr-2" />
                        Share Recording
                      </Button>
                    </div>
                  </div>

                  {showElaborationPrompt && user?.subscription_tier !== 'free' && (
                    <div className="elaboration-prompt">
                      <h3>🎯 Want to Elaborate?</h3>
                      <p>Would you like AI to analyze your brain dump and suggest specific areas to dive deeper?</p>
                      
                      {elaborationIdeas.length === 0 ? (
                        <div className="elaboration-actions">
                          <Button 
                            onClick={generateElaborationIdeas}
                            disabled={isGeneratingIdeas}
                            className="generate-ideas-btn"
                          >
                            {isGeneratingIdeas ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Analyzing your dump...
                              </>
                            ) : (
                              <>
                                Generate 5 Elaboration Ideas
                              </>
                            )}
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => setShowElaborationPrompt(false)}
                          >
                            Skip for now
                          </Button>
                        </div>
                      ) : (
                        <div className="elaboration-ideas">
                          <p className="pro-tip">💡 <strong>PRO TIP:</strong> Click any idea below to elaborate, or ask AI for a list of other ways you can use this content later!</p>
                          {elaborationIdeas.map((idea, idx) => (
                            <div 
                              key={idx} 
                              className="idea-card"
                              onClick={() => elaborateOnIdea(idea)}
                            >
                              {idea}
                            </div>
                          ))}
                          <Button 
                            variant="outline"
                            onClick={() => {
                              setShowElaborationPrompt(false);
                              setElaborationIdeas([]);
                            }}
                            className="close-ideas-btn"
                          >
                            Close
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="action-buttons">
                    <div className="save-buttons">
                      <Button 
                        data-testid="save-mp3-btn"
                        onClick={saveMP3}
                        variant="outline"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Audio
                      </Button>
                    </div>

                    {user?.subscription_tier !== 'free' && (
                      <div className="ai-selector">
                        <Label>
                          {currentSessionId 
                            ? "Continue conversation or start new recording:"
                            : "Send to AI to start a conversation:"
                          }
                        </Label>
                        <Button 
                          data-testid="send-to-ai-btn"
                          onClick={sendToAI}
                          disabled={isSending}
                          className="send-ai-btn"
                          size="lg"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {isSending ? "Sending..." : currentSessionId ? "Add to Chat" : "Send to AI"}
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="chat" className="tab-content" style={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', margin: 0}}>
          <div className="app-main" style={{flex: 1}}>
            {/* Chat History Sidebar - Always visible */}
            <div className="app-sidebar chat-history-sidebar">
              <div className="sidebar-header">
                <h3>AI Chat</h3>
                <div className="sidebar-header-actions">
                  {currentSessionId && (
                    <Button variant="outline" size="sm" onClick={startNewChat}>
                      New Chat
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => { loadSessions(); loadProjects(); }}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* New Project Button */}
              <div className="new-project-section">
                {!showNewProjectDialog ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowNewProjectDialog(true)}
                    className="new-project-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Project
                  </Button>
                ) : (
                  <div className="new-project-dialog">
                    <Input
                      placeholder="Project name..."
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') createProject();
                        if (e.key === 'Escape') { setShowNewProjectDialog(false); setNewProjectName(''); }
                      }}
                      autoFocus
                      className="new-project-input"
                    />
                    <div className="new-project-actions">
                      <Button size="sm" onClick={createProject}>
                        <CheckIcon className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setShowNewProjectDialog(false); setNewProjectName(''); }}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Projects and Sessions List */}
              <div className="sessions-list">
                  {loadingSessions || loadingProjects ? (
                    <div className="loading-sessions">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <>
                      {/* Render Projects */}
                      {projects.map((project) => {
                        const projectSessions = sessions.filter(s => s.project_id === project.id);
                        const isCollapsed = collapsedProjects[project.id];
                        
                        return (
                          <div key={project.id} className="project-folder">
                            <div 
                              className="project-header"
                              onClick={() => toggleProject(project.id)}
                            >
                              {isCollapsed ? (
                                <ChevronRight className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                              <Folder className="w-4 h-4" style={{ color: project.color }} />
                              <span className="project-name">{project.name}</span>
                              <span className="project-count">({projectSessions.length})</span>
                              <button
                                onClick={(e) => deleteProject(project.id, e)}
                                className="project-delete-btn"
                                title="Delete project"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                            
                            {!isCollapsed && (
                              <div className="project-sessions">
                                {projectSessions.length === 0 ? (
                                  <div className="empty-project">No chats in this project</div>
                                ) : (
                                  projectSessions.map((session) => (
                                    <div
                                      key={session.id}
                                      className={`session-item project-session ${currentSessionId === session.id ? 'active' : ''}`}
                                      onClick={(e) => {
                                        // Don't load chat if clicking on buttons or their children
                                        if (e.target.closest('button') || e.target.closest('.session-actions')) {
                                          console.log("Clicked on button area, not loading chat");
                                          return;
                                        }
                                        console.log("Loading chat history for:", session.id);
                                        loadChatHistory(session.id);
                                      }}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        setContextMenu({ x: e.clientX, y: e.clientY, session });
                                      }}
                                    >
                                      {editingSessionId === session.id ? (
                                        <div className="session-editing-full" onClick={(e) => e.stopPropagation()}>
                                          <Input
                                            value={editingSessionTitle}
                                            onChange={(e) => setEditingSessionTitle(e.target.value)}
                                            onKeyPress={(e) => {
                                              if (e.key === 'Enter') {
                                                saveSessionTitle(session.id, e);
                                              } else if (e.key === 'Escape') {
                                                cancelEditingSession(e);
                                              }
                                            }}
                                            className="session-edit-input"
                                            placeholder="Chat title..."
                                            autoFocus
                                          />
                                          <select
                                            className="session-project-select"
                                            value={session.project_id || ''}
                                            onChange={(e) => {
                                              const projectId = e.target.value || null;
                                              moveSessionToProject(session.id, projectId);
                                            }}
                                          >
                                            <option value="">Uncategorized</option>
                                            {projects.map(project => (
                                              <option key={project.id} value={project.id}>
                                                {project.name}
                                              </option>
                                            ))}
                                          </select>
                                          <div className="session-edit-actions">
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              onClick={(e) => saveSessionTitle(session.id, e)}
                                              className="edit-save-btn"
                                              title="Save"
                                            >
                                              <CheckIcon className="w-3 h-3" />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              onClick={cancelEditingSession}
                                              className="edit-cancel-btn"
                                              title="Cancel"
                                            >
                                              <X className="w-3 h-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <div className="session-title-row">
                                            <div className="session-title">{session.title}</div>
                                            <div className="session-actions">
                                              {/* Recording download button - only show for current session with recording */}
                                              {currentSessionId === session.id && lastRecordingBlob && (
                                                <>
                                                  <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      e.preventDefault();
                                                      downloadRecording();
                                                    }}
                                                    className="session-action-btn"
                                                    title="Download recording"
                                                  >
                                                    <Mic className="w-3 h-3" style={{color: '#0ea5e9'}} />
                                                  </Button>
                                                  <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      e.preventDefault();
                                                      shareRecording();
                                                    }}
                                                    className="session-action-btn"
                                                    title="Share recording"
                                                  >
                                                    <Save className="w-3 h-3" style={{color: '#10b981'}} />
                                                  </Button>
                                                </>
                                              )}
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  e.preventDefault();
                                                  setEditingTags(session);
                                                  setTagInput((session.tags || []).join(', '));
                                                }}
                                                className="session-action-btn session-tag-btn"
                                                title="Add tags"
                                              >
                                                <Tag className="w-3 h-3" />
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={(e) => startEditingSession(session, e)}
                                                className="session-action-btn session-edit-btn"
                                                title="Edit chat"
                                              >
                                                <Edit2 className="w-3 h-3" />
                                              </Button>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  e.preventDefault();
                                                  deleteSession(session.id, e);
                                                }}
                                                className="session-action-btn session-delete-btn native-btn"
                                                title="Delete chat"
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </button>
                                            </div>
                                          </div>
                                          <div className="session-meta">
                                            <span>{session.message_count} messages</span>
                                            {session.tags && session.tags.length > 0 && (
                                              <div className="session-tags">
                                                {session.tags.map((tag, idx) => (
                                                  <span key={idx} className="tag-badge">#{tag}</span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Uncategorized Sessions */}
                      {(() => {
                        const uncategorizedSessions = sessions.filter(s => !s.project_id);
                        const isCollapsed = collapsedProjects['uncategorized'];
                        
                        if (uncategorizedSessions.length === 0 && projects.length > 0) return null;
                        
                        return (
                          <div className="project-folder">
                            <div 
                              className="project-header"
                              onClick={() => toggleProject('uncategorized')}
                            >
                              {isCollapsed ? (
                                <ChevronRight className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                              <Folder className="w-4 h-4" style={{ color: '#94a3b8' }} />
                              <span className="project-name">Uncategorized</span>
                              <span className="project-count">({uncategorizedSessions.length})</span>
                            </div>
                            
                            {!isCollapsed && (
                              <div className="project-sessions">
                                {uncategorizedSessions.length === 0 ? (
                                  <div className="empty-project">No uncategorized chats</div>
                                ) : (
                                  uncategorizedSessions.map((session) => (
                      <div
                        key={session.id}
                        className={`session-item ${currentSessionId === session.id ? 'active' : ''}`}
                        onClick={(e) => {
                          // Only load chat if not clicking on action buttons
                          if (!e.target.closest('.session-actions')) {
                            loadChatHistory(session.id);
                          }
                        }}
                      >
                        {editingSessionId === session.id ? (
                          <div className="session-editing-full" onClick={(e) => e.stopPropagation()}>
                            <Input
                              value={editingSessionTitle}
                              onChange={(e) => setEditingSessionTitle(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  saveSessionTitle(session.id, e);
                                } else if (e.key === 'Escape') {
                                  cancelEditingSession(e);
                                }
                              }}
                              className="session-edit-input"
                              placeholder="Chat title..."
                              autoFocus
                            />
                            <select
                              className="session-project-select"
                              value={session.project_id || ''}
                              onChange={(e) => {
                                const projectId = e.target.value || null;
                                moveSessionToProject(session.id, projectId);
                              }}
                            >
                              <option value="">Uncategorized</option>
                              {projects.map(project => (
                                <option key={project.id} value={project.id}>
                                  {project.name}
                                </option>
                              ))}
                            </select>
                            <div className="session-edit-actions">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => saveSessionTitle(session.id, e)}
                                className="edit-save-btn"
                                title="Save"
                              >
                                <CheckIcon className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelEditingSession}
                                className="edit-cancel-btn"
                                title="Cancel"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="session-title-row">
                              <div className="session-title">{session.title}</div>
                              <div className="session-actions">
                                {/* Recording download button - only show for current session with recording */}
                                {currentSessionId === session.id && lastRecordingBlob && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        downloadRecording();
                                      }}
                                      className="session-action-btn"
                                      title="Download recording"
                                    >
                                      <Mic className="w-3 h-3" style={{color: '#0ea5e9'}} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        shareRecording();
                                      }}
                                      className="session-action-btn"
                                      title="Share recording"
                                    >
                                      <Save className="w-3 h-3" style={{color: '#10b981'}} />
                                    </Button>
                                  </>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setEditingTags(session);
                                    setTagInput((session.tags || []).join(', '));
                                  }}
                                  className="session-action-btn session-tag-btn"
                                  title="Add tags"
                                >
                                  <Tag className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => startEditingSession(session, e)}
                                  className="session-action-btn session-edit-btn"
                                  title="Edit chat"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    deleteSession(session.id, e);
                                  }}
                                  className="session-action-btn session-delete-btn native-btn"
                                  title="Delete chat"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <div className="session-meta">
                                  <span>{session.message_count} messages</span>
                                  {session.tags && session.tags.length > 0 && (
                                    <div className="session-tags">
                                      {session.tags.map((tag, idx) => (
                                        <span key={idx} className="tag-badge">#{tag}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* Context Menu for Right-Click */}
      {contextMenu && (
        <>
          <div 
            className="context-menu-overlay"
            onClick={() => setContextMenu(null)}
          />
          <div 
            className="context-menu"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            <div className="context-menu-section">
              <div className="context-menu-label">Move to Project</div>
              {projects.map(project => (
                <button
                  key={project.id}
                  className="context-menu-item"
                  onClick={() => {
                    moveSessionToProject(contextMenu.session.id, project.id);
                    setContextMenu(null);
                  }}
                >
                  <Folder className="w-3 h-3" style={{ color: project.color }} />
                  {project.name}
                </button>
              ))}
              <button
                className="context-menu-item"
                onClick={() => {
                  moveSessionToProject(contextMenu.session.id, null);
                  setContextMenu(null);
                }}
              >
                <Folder className="w-3 h-3" style={{ color: '#94a3b8' }} />
                Uncategorized
              </button>
            </div>
            <div className="context-menu-divider" />
            <div className="context-menu-section">
              <button
                className="context-menu-item"
                onClick={() => {
                  setEditingTags(contextMenu.session);
                  setTagInput((contextMenu.session.tags || []).join(', '));
                  setContextMenu(null);
                }}
              >
                <Tag className="w-3 h-3" />
                Edit Tags
              </button>
            </div>
          </div>
        </>
      )}

      {/* Tag Editor Dialog */}
      {editingTags && (
        <div className="modal-overlay" onClick={() => setEditingTags(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Tags for "{editingTags.title}"</h3>
            <Input
              placeholder="tag1, tag2, tag3..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const tags = tagInput.split(',').map(t => t.trim()).filter(t => t);
                  updateSessionTags(editingTags.id, tags);
                  setEditingTags(null);
                  setTagInput('');
                }
              }}
            />
            <div className="modal-actions">
              <Button onClick={() => {
                const tags = tagInput.split(',').map(t => t.trim()).filter(t => t);
                updateSessionTags(editingTags.id, tags);
                setEditingTags(null);
                setTagInput('');
              }}>
                Save
              </Button>
              <Button variant="outline" onClick={() => setEditingTags(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>

            {/* Main Chat Area */}
            <div className="chat-main-area">
              {/* Recording Section - Hidden, now integrated in bottom input */}
              {false && (
              <Card className="recording-card-compact">
                <CardContent className="recording-content-compact">
                  <div className="recording-header">
                    <div className="recording-title-group">
                      <span className="recording-title">Dictate</span>
                      <div className="help-icon-wrapper">
                        <HelpCircle className="help-icon" />
                        <div className="ideas-framework-tooltip">
                          <h4>💡 DUMPHUB Recording Tip</h4>
                          <p><strong>Set Your Intention Before You Start</strong></p>
                          <p>Take a moment to think about:</p>
                          <ul>
                            <li>What do you want to accomplish with this brain dump?</li>
                            <li>What's your main goal or challenge?</li>
                            <li>What ideas or research are you capturing?</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    {user?.subscription_tier === 'free' && (
                      <span className="transcription-count">{user.transcriptions_today}/10 today</span>
                    )}
                  </div>

                  <div className="record-button-container-compact">
                    {!isRecording ? (
                      <Button
                        data-testid="start-recording-btn"
                        onClick={startRecording}
                        className="record-btn-large dump-btn"
                      >
                        <Mic className="w-10 h-10" />
                        <span className="btn-label-large">DUMP</span>
                      </Button>
                    ) : (
                      <div className="recording-controls">
                        {!isPaused ? (
                          <Button
                            data-testid="pause-recording-btn"
                            onClick={pauseRecording}
                            className="control-btn pause-btn"
                          >
                            <Pause className="w-6 h-6" />
                            <span>Pause</span>
                          </Button>
                        ) : (
                          <Button
                            data-testid="resume-recording-btn"
                            onClick={resumeRecording}
                            className="control-btn resume-btn"
                          >
                            <Play className="w-6 h-6" />
                            <span>Resume</span>
                          </Button>
                        )}
                        <Button
                          data-testid="stop-recording-btn"
                          onClick={stopAndTranscribe}
                          className="control-btn stop-btn"
                        >
                          <Square className="w-6 h-6" />
                          <span>Stop</span>
                        </Button>
                      </div>
                    )}
                  </div>

                  {isRecording && (
                    <div className="recording-indicator">
                      <span className={`pulse ${isPaused ? 'paused' : ''}`}></span>
                      <span>{isPaused ? 'Paused' : 'Recording'}... {formatTime(recordingTime)}</span>
                    </div>
                  )}

                  {isTranscribing && (
                    <div className="processing-flow">
                      <Loader2 className="w-8 h-8 animate-spin" />
                      <p className="processing-text">Transcribing your brain dump...</p>
                    </div>
                  )}

                  {transcript && (
                    <>
                      <div className="transcript-container">
                        <Label>Your Brain Dump:</Label>
                        <div className="transcript-box" data-testid="transcript-display">
                          {transcript}
                        </div>
                      </div>

                      {user?.subscription_tier !== 'free' && (
                        <div className="action-buttons">
                          <Button 
                            data-testid="send-to-ai-btn"
                            onClick={sendToAI}
                            disabled={isSending}
                            className="send-ai-btn"
                            size="lg"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {isSending ? "Sending..." : currentSessionId ? "Add to Chat" : "Send to AI"}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Transcript Display Area */}
              {(isTranscribing || transcript) && (
                <Card className="transcript-display-card">
                  <CardContent>
                    {isTranscribing && (
                      <div className="processing-flow">
                        <Loader2 className="w-8 h-8 animate-spin" />
                        <p className="processing-text">Transcribing your brain dump...</p>
                      </div>
                    )}

                    {transcript && !isTranscribing && (
                      <>
                        <div className="transcript-container">
                          <Label>Your Brain Dump:</Label>
                          <div className="transcript-box" data-testid="transcript-display">
                            {transcript}
                          </div>
                        </div>

                        {user?.subscription_tier !== 'free' && (
                          <div className="action-buttons">
                            <Button 
                              data-testid="send-to-ai-btn"
                              onClick={sendToAI}
                              disabled={isSending}
                              className="send-ai-btn"
                              size="lg"
                            >
                              <Send className="w-4 h-4 mr-2" />
                              {isSending ? "Sending..." : currentSessionId ? "Add to Chat" : "Send to AI"}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Chat Area */}
              <Card className="chat-card chat-card-full">
              <CardHeader className="chat-card-header-compact">
                <CardTitle className="chat-title-compact">
                  {chatMessages.length === 0 && user?.subscription_tier !== 'free' 
                    ? 'Start Your Chat' 
                    : 'Chat Messages'
                  }
                </CardTitle>
              </CardHeader>
            <CardContent className="chat-card-content">
              <div className="chat-container">
                <div className="chat-messages" data-testid="chat-messages">
                  {chatMessages.length === 0 ? (
                    <div className="empty-chat">
                      <MessageSquare className="w-12 h-12 opacity-20" />
                      <p>
                        {user?.subscription_tier === 'free'
                          ? 'Chat is available on paid plans'
                          : 'No messages yet. Record your voice or type a message to start chatting with AI.'
                        }
                      </p>
                    </div>
                  ) : (
                    <>
                      {chatMessages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`chat-message ${msg.role}`}
                          data-testid={`chat-message-${msg.role}`}
                        >
                          <div className="message-content">
                            {msg.content}
                            {msg.image_base64 && (
                              <div style={{marginTop: '0.75rem'}}>
                                <img 
                                  src={`data:image/png;base64,${msg.image_base64}`}
                                  alt="Generated"
                                  style={{
                                    maxWidth: '100%',
                                    borderRadius: '0.5rem',
                                    border: '1px solid #e5e7eb',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                  }}
                                />
                                <a
                                  href={`data:image/png;base64,${msg.image_base64}`}
                                  download={`dumphub-image-${Date.now()}.png`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toast.success("Image downloaded!");
                                  }}
                                  style={{
                                    marginTop: '0.5rem',
                                    width: '100%',
                                    height: '36px',
                                    background: 'transparent',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '0.375rem',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    textDecoration: 'none',
                                    color: 'inherit'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                  <FileText className="w-4 h-4" />
                                  Download Image
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                    </>
                  )}
                </div>
                
                {user?.subscription_tier !== 'free' && (
                  <div className="chat-input-container" style={{display: 'none'}}>
                    {!isRecording ? (
                      <Button
                        data-testid="dump-btn-chat"
                        onClick={startRecording}
                        className="dump-btn-chat"
                        variant="outline"
                      >
                        <Mic className="w-5 h-5" />
                      </Button>
                    ) : (
                      <div className="recording-controls-inline">
                        {!isPaused ? (
                          <Button
                            onClick={pauseRecording}
                            className="control-btn-inline pause-btn-inline"
                            variant="outline"
                            size="sm"
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            onClick={resumeRecording}
                            className="control-btn-inline resume-btn-inline"
                            size="sm"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          onClick={stopAndTranscribe}
                          className="control-btn-inline stop-btn-inline"
                          variant="outline"
                          size="sm"
                        >
                          <Square className="w-4 h-4" />
                        </Button>
                        <span className="recording-time">{formatTime(recordingTime)}</span>
                      </div>
                    )}
                    <Input
                      data-testid="chat-input"
                      placeholder="Type your message..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendChatMessage();
                        }
                      }}
                      disabled={isSending || isRecording}
                    />
                    <Button 
                      data-testid="send-chat-btn"
                      onClick={sendChatMessage}
                      disabled={isSending || !chatInput.trim() || isRecording}
                      className="send-chat-btn"
                    >
                      {isSending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          </div>
          </div>
        
        {/* Image Generation Dialog */}
        {showImageDialog && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001
            }}
            onClick={() => !generatingImage && setShowImageDialog(false)}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '0.75rem',
                padding: '2rem',
                maxWidth: '700px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
            >
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: '700', color: '#111827'}}>
                  🎨 Generate Image
                </h3>
                <button
                  onClick={() => setShowImageDialog(false)}
                  disabled={generatingImage}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    color: '#6b7280'
                  }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!generatedImage ? (
                <>
                  <p style={{fontSize: '0.95rem', color: '#6b7280', marginBottom: '1.5rem'}}>
                    Choose an image format or enter a custom prompt:
                  </p>

                  <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem'}}>
                    {imageTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setSelectedImageType(type.id)}
                        style={{
                          textAlign: 'left',
                          padding: '1rem',
                          background: selectedImageType === type.id ? '#cffafe' : '#f9fafb',
                          border: `2px solid ${selectedImageType === type.id ? '#0891b2' : '#e5e7eb'}`,
                          borderRadius: '0.5rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{fontWeight: '600', color: '#111827', marginBottom: '0.25rem'}}>
                          {type.name}
                        </div>
                        <div style={{fontSize: '0.875rem', color: '#6b7280'}}>
                          {type.description}
                        </div>
                      </button>
                    ))}
                  </div>

                  {selectedImageType === 'custom' && (
                    <div style={{marginBottom: '1.5rem'}}>
                      <label style={{display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#111827'}}>
                        Custom Image Prompt:
                      </label>
                      <textarea
                        value={customImagePrompt}
                        onChange={(e) => setCustomImagePrompt(e.target.value)}
                        placeholder="Describe the image you want to create..."
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          fontSize: '0.95rem',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>
                  )}

                  {selectedImageType && selectedImageType !== 'custom' && (
                    <div style={{marginBottom: '1.5rem'}}>
                      <label style={{display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#111827'}}>
                        Style (optional):
                      </label>
                      <input
                        type="text"
                        value={imageStyle}
                        onChange={(e) => setImageStyle(e.target.value)}
                        placeholder="e.g., professional, creative, minimalist, vibrant"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          fontSize: '0.95rem'
                        }}
                      />
                    </div>
                  )}

                  <Button
                    onClick={generateImage}
                    disabled={generatingImage || (!selectedImageType && !customImagePrompt.trim())}
                    style={{
                      width: '100%',
                      height: '48px',
                      background: '#0891b2',
                      color: 'white',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {generatingImage ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Generating Image...</span>
                      </>
                    ) : (
                      <span>Generate Image</span>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <div style={{marginBottom: '1.5rem', textAlign: 'center'}}>
                    <img 
                      src={`data:image/png;base64,${generatedImage}`}
                      alt="Generated"
                      style={{maxWidth: '100%', borderRadius: '0.5rem', border: '1px solid #e5e7eb'}}
                    />
                  </div>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                    <Button
                      onClick={async () => {
                        // Add image to chat
                        const imageMessage = {
                          role: 'assistant',
                          content: '[Generated Image]',
                          image_base64: generatedImage,
                          timestamp: new Date().toISOString()
                        };
                        setChatMessages(prev => [...prev, imageMessage]);
                        toast.success("Image added to chat!");
                        setShowImageDialog(false);
                        setGeneratedImage(null);
                        setSelectedImageType(null);
                      }}
                      style={{
                        height: '48px',
                        background: '#0891b2',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '1rem'
                      }}
                    >
                      Add to Chat
                    </Button>
                    <div style={{display: 'flex', gap: '0.75rem'}}>
                      <a
                        href={`data:image/png;base64,${generatedImage}`}
                        download={`dumphub-image-${Date.now()}.png`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toast.success("Image downloaded!");
                        }}
                        style={{
                          flex: 1,
                          height: '44px',
                          padding: '0 1rem',
                          background: 'transparent',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontSize: '0.95rem',
                          fontWeight: '500',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          textDecoration: 'none',
                          color: 'inherit'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        Download Image
                      </a>
                      <Button
                        onClick={() => {
                          setGeneratedImage(null);
                          setSelectedImageType(null);
                        }}
                        variant="outline"
                        style={{
                          flex: 1,
                          height: '44px'
                        }}
                      >
                        Generate Another
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Elaboration Ideas Modal */}
        {showElaborationPrompt && elaborationIdeas.length > 0 && (
          <div 
            style={{
              position: 'fixed',
              bottom: '80px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              padding: '1.5rem',
              maxWidth: '800px',
              width: '90%',
              zIndex: 1000
            }}
          >
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
              <h3 style={{fontSize: '1.125rem', fontWeight: '700', color: '#111827'}}>
                💡 Elaboration Ideas
              </h3>
              <button
                onClick={() => setShowElaborationPrompt(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: '#6b7280'
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem'}}>
              Click any suggestion to add it to your prompt:
            </p>
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
              {elaborationIdeas.map((idea, idx) => (
                <button
                  key={idx}
                  onClick={() => useElaborationIdea(idea)}
                  style={{
                    textAlign: 'left',
                    padding: '1rem',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontSize: '0.95rem',
                    color: '#111827',
                    lineHeight: '1.5'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#8b5cf6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <span style={{fontWeight: '600', color: '#8b5cf6', marginRight: '0.5rem'}}>
                    {idx + 1}.
                  </span>
                  {idea}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Fixed Footer with Chat Input */}
        {activeTab === 'chat' && (
          <div className="app-footer">
            <div className="app-footer-inner">
              <div className="app-footer-input">
                {!isRecording ? (
                  <>
                    <Button
                      data-testid="dump-btn-footer"
                      onClick={startRecording}
                      className="dump-btn-footer"
                      variant="outline"
                      style={{
                        height: '44px',
                        padding: '0 1rem',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        borderColor: '#0891b2',
                        color: '#0891b2'
                      }}
                      title="DUMP - Record your voice"
                    >
                      <span>DUMP</span>
                      <Mic className="w-5 h-5" />
                    </Button>
                    <Button
                      data-testid="elaborate-btn-footer"
                      onClick={generateElaborationIdeas}
                      disabled={isGeneratingIdeas || chatMessages.filter(m => m.role === 'assistant').length === 0}
                      variant="outline"
                      style={{
                        height: '44px',
                        padding: '0 1rem',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        borderColor: '#8b5cf6',
                        color: '#8b5cf6',
                        opacity: (isGeneratingIdeas || chatMessages.filter(m => m.role === 'assistant').length === 0) ? 0.5 : 1
                      }}
                      title="Get 5 elaboration ideas based on AI's last response"
                    >
                      {isGeneratingIdeas ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <span>Elaborate</span>
                      )}
                    </Button>
                    <Button
                      data-testid="image-btn-footer"
                      onClick={openImageDialog}
                      disabled={chatMessages.filter(m => m.role === 'assistant').length === 0}
                      variant="outline"
                      style={{
                        height: '44px',
                        padding: '0 1rem',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        borderColor: '#0891b2',
                        color: '#0891b2',
                        opacity: (chatMessages.filter(m => m.role === 'assistant').length === 0) ? 0.5 : 1
                      }}
                      title="Generate AI image based on last response"
                    >
                      <span>Make A Image</span>
                    </Button>
                  </>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0 1rem',
                    height: '44px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    background: '#fef3c7'
                  }}>
                    {!isPaused ? (
                      <Button
                        onClick={pauseRecording}
                        variant="outline"
                        size="sm"
                        style={{height: '32px'}}
                      >
                        <Pause className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        onClick={resumeRecording}
                        size="sm"
                        style={{height: '32px'}}
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      onClick={stopAndTranscribe}
                      variant="outline"
                      size="sm"
                      style={{height: '32px'}}
                    >
                      <Square className="w-4 h-4" />
                    </Button>
                    <span style={{
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      color: '#92400e'
                    }}>{formatTime(recordingTime)}</span>
                  </div>
                )}
                <textarea
                  data-testid="chat-input-footer"
                  placeholder="Type your message or paste a URL..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendChatMessage();
                    }
                  }}
                  disabled={isSending || isRecording}
                  rows={1}
                  style={{minHeight: '44px', maxHeight: '200px'}}
                />
                <Button 
                  data-testid="send-chat-btn-footer"
                  onClick={sendChatMessage}
                  disabled={isSending || !chatInput.trim() || isRecording}
                  className="send-chat-btn"
                  style={{
                    height: '44px',
                    padding: '0 1rem',
                    background: '#0891b2',
                    color: 'white'
                  }}
                >
                  {isSending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        </TabsContent>

        <TabsContent value="subscription" className="tab-content">
          <Card className="subscription-card">
            <CardHeader>
              <CardTitle>Manage Subscription</CardTitle>
              <CardDescription>
                Current plan: <strong>{user?.subscription_tier?.toUpperCase()}</strong>
                {user?.email?.toLowerCase() === 'kenpcox@gmail.com' && (
                  <span className="owner-note"> (Owner Account - Complimentary Pro)</span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {user?.email?.toLowerCase() === 'kenpcox@gmail.com' ? (
                <div className="owner-status">
                  <div className="info-box owner-box">
                    <h3>👑 Owner Account</h3>
                    <p>You have complimentary Pro access with unlimited usage.</p>
                    <ul className="owner-benefits">
                      <li>✓ Full admin dashboard access</li>
                      <li>✓ 10M tokens/month (never charged)</li>
                      <li>✓ Unlimited transcriptions</li>
                      <li>✓ Priority support</li>
                      <li>✓ All features unlocked</li>
                    </ul>
                  </div>
                </div>
              ) : user?.subscription_tier === 'free' ? (
                <div className="upgrade-section">
                  <h3>Upgrade to unlock more features</h3>
                  <div className="upgrade-plans">
                    <div className="upgrade-plan">
                      <h4>Basic Plan</h4>
                      <p className="plan-price">$20/month</p>
                      <ul>
                        <li><Check className="w-4 h-4" /> 1M tokens included</li>
                        <li><Check className="w-4 h-4" /> Unlimited transcriptions</li>
                        <li><Check className="w-4 h-4" /> AI chat included</li>
                      </ul>
                      <Button onClick={() => handleUpgrade('basic')} className="upgrade-btn">
                        Upgrade to Basic
                      </Button>
                    </div>
                    <div className="upgrade-plan">
                      <h4>Pro Plan</h4>
                      <p className="plan-price">$99/month</p>
                      <ul>
                        <li><Check className="w-4 h-4" /> 10M tokens included</li>
                        <li><Check className="w-4 h-4" /> Priority support</li>
                        <li><Check className="w-4 h-4" /> Advanced features</li>
                      </ul>
                      <Button onClick={() => handleUpgrade('pro')} className="upgrade-btn">
                        Upgrade to Pro
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="token-management">
                  <div className="token-stats">
                    <div className="stat-item">
                      <span className="stat-label">Tokens Used:</span>
                      <span className="stat-value">{user.tokens_used.toLocaleString()}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Tokens Remaining:</span>
                      <span className="stat-value">{availableTokens.toLocaleString()}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Additional Purchases:</span>
                      <span className="stat-value">{user.token_purchases} × 1M</span>
                    </div>
                  </div>
                  <div className="buy-tokens-section">
                    <h3>Need more tokens?</h3>
                    <p>Purchase an additional 1 million tokens for $20</p>
                    <Button onClick={handleBuyTokens} className="buy-tokens-btn">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Buy 1M Tokens ($20)
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="tab-content" style={{padding: '2rem', height: '100%', overflowY: 'auto'}}>
          <div style={{maxWidth: '1200px', margin: '0 auto', paddingBottom: '4rem'}}>
            <h2 style={{fontSize: '1.5rem', fontWeight: '700', marginBottom: '1.5rem', marginTop: 0}}>Settings</h2>
            
            <Tabs defaultValue="subscription" style={{width: '100%'}}>
              <TabsList style={{marginBottom: '1.5rem', display: 'flex', gap: '0.5rem'}}>
                <TabsTrigger value="subscription">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Subscription
                </TabsTrigger>
                <TabsTrigger value="api-keys">
                  <FileText className="w-4 h-4 mr-2" />
                  API Keys
                </TabsTrigger>
                <TabsTrigger value="connections">
                  <Shield className="w-4 h-4 mr-2" />
                  Connections
                </TabsTrigger>
              </TabsList>
              
              {/* Subscription Tab */}
              <TabsContent value="subscription">
                <Card>
                  <CardHeader>
                    <CardTitle>Your Subscription</CardTitle>
                    <CardDescription>Manage your subscription plan and billing</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="subscription-info" style={{marginBottom: '2rem'}}>
                      <div className="current-plan" style={{padding: '1.5rem', background: '#f8fafc', borderRadius: '0.5rem', marginBottom: '1.5rem'}}>
                        <h3 style={{fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem'}}>
                          Current Plan: <span className="plan-badge" style={{background: '#0ea5e9', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '0.25rem', marginLeft: '0.5rem'}}>{user?.subscription_tier?.toUpperCase()}</span>
                        </h3>
                        <p style={{color: '#64748b', marginTop: '0.5rem'}}>
                          {user?.subscription_tier === 'free' && 'Upgrade to access unlimited transcriptions and AI features.'}
                          {user?.subscription_tier === 'basic' && '1M tokens included per month. Buy more tokens as needed.'}
                          {user?.subscription_tier === 'pro' && '10M tokens included per month with priority support.'}
                        </p>
                      </div>
                      
                      {user?.subscription_tier === 'free' && (
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem'}}>
                          <div style={{padding: '1.5rem', border: '2px solid #e2e8f0', borderRadius: '0.5rem'}}>
                            <h4 style={{fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem'}}>Basic Plan</h4>
                            <p style={{fontSize: '2rem', fontWeight: '700', color: '#0ea5e9', marginBottom: '0.5rem'}}>$20<span style={{fontSize: '1rem', color: '#64748b'}}>/mo</span></p>
                            <ul style={{listStyle: 'none', padding: 0, marginBottom: '1rem', color: '#64748b'}}>
                              <li style={{marginBottom: '0.25rem'}}>✓ 1M tokens included</li>
                              <li style={{marginBottom: '0.25rem'}}>✓ Unlimited transcriptions</li>
                              <li style={{marginBottom: '0.25rem'}}>✓ AI chat included</li>
                            </ul>
                            <Button style={{width: '100%', background: '#0ea5e9'}}>Upgrade to Basic</Button>
                          </div>
                          
                          <div style={{padding: '1.5rem', border: '2px solid #0ea5e9', borderRadius: '0.5rem', background: '#f0f9ff'}}>
                            <div style={{background: '#0ea5e9', color: 'white', fontSize: '0.75rem', fontWeight: '600', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', display: 'inline-block', marginBottom: '0.5rem'}}>POPULAR</div>
                            <h4 style={{fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem'}}>Pro Plan</h4>
                            <p style={{fontSize: '2rem', fontWeight: '700', color: '#0ea5e9', marginBottom: '0.5rem'}}>$99<span style={{fontSize: '1rem', color: '#64748b'}}>/mo</span></p>
                            <ul style={{listStyle: 'none', padding: 0, marginBottom: '1rem', color: '#64748b'}}>
                              <li style={{marginBottom: '0.25rem'}}>✓ 10M tokens included</li>
                              <li style={{marginBottom: '0.25rem'}}>✓ Priority support</li>
                              <li style={{marginBottom: '0.25rem'}}>✓ Advanced features</li>
                            </ul>
                            <Button style={{width: '100%', background: '#0ea5e9'}}>Upgrade to Pro</Button>
                          </div>
                        </div>
                      )}
                      
                      {user?.subscription_tier !== 'free' && (
                        <div>
                          <Button variant="outline" style={{marginRight: '1rem'}}>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Manage Billing
                          </Button>
                          <Button variant="outline">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Change Plan
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* API Keys Tab */}
              <TabsContent value="api-keys">
                <Card className="settings-card">
                  <CardHeader>
                    <CardTitle>API Key Management</CardTitle>
              <CardDescription>
                {user?.subscription_tier === 'free' 
                  ? 'Free users: Add your own OpenAI API key to use transcription features'
                  : 'Your subscription includes built-in API access. No key required.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {user?.subscription_tier === 'free' ? (
                <div className="api-key-management">
                  <div className="api-key-section">
                    <Label htmlFor="user-openai-key">Your OpenAI API Key</Label>
                    <p className="key-help-text">
                      Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">OpenAI Platform</a>
                    </p>
                    <Input
                      id="user-openai-key"
                      data-testid="user-openai-key-input"
                      type="password"
                      placeholder="sk-..."
                      value={userApiKey}
                      onChange={(e) => setUserApiKey(e.target.value)}
                    />
                    {userApiKey && (
                      <span className="key-status-success">
                        ✓ API key configured
                      </span>
                    )}
                  </div>

                  <div className="key-actions">
                    <Button 
                      data-testid="save-api-key-btn"
                      onClick={saveUserApiKey}
                      disabled={isSavingKey}
                      className="save-key-btn"
                    >
                      {isSavingKey ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save API Key
                        </>
                      )}
                    </Button>
                    {userApiKey && (
                      <Button 
                        variant="outline"
                        onClick={deleteUserApiKey}
                        className="delete-key-btn"
                      >
                        Delete Key
                      </Button>
                    )}
                  </div>

                  <div className="key-security-note">
                    <p><strong>Security Note:</strong> Your API key is encrypted and stored securely. It's only used for your transcription requests.</p>
                  </div>
                </div>
              ) : (
                <div className="paid-user-info">
                  <div className="info-box">
                    <h3>✓ API Access Included</h3>
                    <p>Your {user?.subscription_tier} plan includes built-in OpenAI API access. You don't need to provide your own API key.</p>
                    <p className="info-detail">All transcription and chat features are powered by our managed API infrastructure.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
              </TabsContent>
              
              {/* Connections Tab - Facebook Integration */}
              <TabsContent value="connections">
                <Card>
                  <CardHeader>
                    <CardTitle>Social Media Connections</CardTitle>
                    <CardDescription>Connect your social media accounts to syndicate content</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div style={{padding: '1.5rem', border: '2px solid #e2e8f0', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                        <div style={{width: '48px', height: '48px', background: '#1877f2', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.5rem', fontWeight: '700'}}>
                          f
                        </div>
                        <div>
                          <h4 style={{fontSize: '1rem', fontWeight: '600', marginBottom: '0.25rem'}}>Facebook</h4>
                          <p style={{color: '#64748b', fontSize: '0.875rem'}}>
                            {user?.facebook_connected ? (
                              <span style={{color: '#10b981'}}>✓ Connected</span>
                            ) : (
                              'Not connected - Connect to post AI-generated content'
                            )}
                          </p>
                        </div>
                      </div>
                      <Button 
                        style={{background: user?.facebook_connected ? '#ef4444' : '#1877f2', color: 'white'}}
                        onClick={() => toast.info('Facebook integration coming soon!')}
                      >
                        {user?.facebook_connected ? 'Disconnect' : 'Connect Facebook'}
                      </Button>
                    </div>
                    
                    <div style={{marginTop: '1.5rem', padding: '1rem', background: '#f0f9ff', borderRadius: '0.5rem', border: '1px solid #0ea5e9'}}>
                      <h5 style={{fontSize: '0.875rem', fontWeight: '600', color: '#0369a1', marginBottom: '0.5rem'}}>📱 Coming Soon: Syndicate Feature</h5>
                      <p style={{fontSize: '0.875rem', color: '#0c4a6e'}}>
                        Once connected, you'll be able to create AI-generated Facebook posts with images and text directly from your chats. Just click "Syndicate" and publish instantly!
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}

// Admin Dashboard Component
function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState("all");
  
  // New coupon form
  const [newCoupon, setNewCoupon] = useState({
    code: "",
    discount_percent: 10,
    max_uses: 100
  });
  
  // New affiliate form
  const [newAffiliate, setNewAffiliate] = useState({
    user_id: "",
    paypal_email: ""
  });

  // System settings
  const [settings, setSettings] = useState({
    openai_key: "",
    stripe_secret_key: "",
    stripe_publishable_key: ""
  });

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/dashboard');
      toast.error("Admin access required");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      const [statsRes, usersRes, couponsRes, affiliatesRes, payoutsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/coupons`, { withCredentials: true }),
        axios.get(`${API}/admin/affiliates`, { withCredentials: true }),
        axios.get(`${API}/admin/affiliates/payouts`, { withCredentials: true }),
        axios.get(`${API}/admin/settings`, { withCredentials: true })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setCoupons(couponsRes.data);
      setAffiliates(affiliatesRes.data);
      setPendingPayouts(payoutsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error("Error loading admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/suspend`, {}, { withCredentials: true });
      toast.success("User suspended");
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to suspend user");
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/activate`, {}, { withCredentials: true });
      toast.success("User activated");
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to activate user");
    }
  };

  const handleCreateCoupon = async () => {
    try {
      await axios.post(`${API}/admin/coupons`, newCoupon, { withCredentials: true });
      toast.success("Coupon created");
      setNewCoupon({ code: "", discount_percent: 10, max_uses: 100 });
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create coupon");
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`, { withCredentials: true });
      toast.success("Coupon deleted");
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to delete coupon");
    }
  };

  const handleCreateAffiliate = async () => {
    try {
      await axios.post(`${API}/admin/affiliates`, newAffiliate, { withCredentials: true });
      toast.success("Affiliate created");
      setNewAffiliate({ user_id: "", paypal_email: "" });
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create affiliate");
    }
  };

  const handleMarkPaid = async (affiliateId, amount) => {
    try {
      await axios.post(`${API}/admin/affiliates/${affiliateId}/mark-paid`, 
        { amount },
        { withCredentials: true }
      );
      toast.success("Marked as paid");
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to mark as paid");
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await axios.post(`${API}/admin/settings`, settings, { withCredentials: true });
      toast.success("Settings updated");
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  const filteredUsers = userFilter === "all" 
    ? users 
    : userFilter === "paid"
    ? users.filter(u => u.subscription_tier !== "free")
    : userFilter === "suspended"
    ? users.filter(u => u.is_suspended)
    : users.filter(u => !u.is_suspended);

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Admin Dashboard</h1>
          <p className="admin-subtitle">Manage your DUMPHUB.AI platform</p>
          <p style={{fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem'}}>Version 2.1</p>
        </div>
        <div className="header-actions">
          <Button variant="outline" onClick={() => navigate('/dashboard')} size="sm">
            Back to App
          </Button>
          <Button variant="outline" onClick={logout} size="sm">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="admin-tabs">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="coupons">Coupons</TabsTrigger>
          <TabsTrigger value="affiliates">Affiliates</TabsTrigger>
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="admin-content">
          <div className="stats-grid">
            <Card>
              <CardHeader>
                <CardTitle>Total Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="stat-number">{stats?.total_users || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Active Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="stat-number">{stats?.active_users || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Paid Subscribers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="stat-number">{stats?.paid_users || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Total Tokens Used</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="stat-number">{(stats?.total_tokens_used || 0).toLocaleString()}</div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users" className="admin-content">
          <Card>
            <CardHeader>
              <CardTitle>User Management</CardTitle>
              <div className="filter-buttons">
                <Button variant={userFilter === "all" ? "default" : "outline"} onClick={() => setUserFilter("all")}>All</Button>
                <Button variant={userFilter === "active" ? "default" : "outline"} onClick={() => setUserFilter("active")}>Active</Button>
                <Button variant={userFilter === "paid" ? "default" : "outline"} onClick={() => setUserFilter("paid")}>Paid</Button>
                <Button variant={userFilter === "suspended" ? "default" : "outline"} onClick={() => setUserFilter("suspended")}>Suspended</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="users-table">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Plan</th>
                      <th>Tokens Used</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.id}>
                        <td>{u.email}</td>
                        <td>{u.name}</td>
                        <td><span className={`plan-badge ${u.subscription_tier}`}>{u.subscription_tier}</span></td>
                        <td>{u.tokens_used.toLocaleString()}</td>
                        <td>{u.is_suspended ? <span className="status-suspended">Suspended</span> : <span className="status-active">Active</span>}</td>
                        <td>
                          {u.is_suspended ? (
                            <Button size="sm" onClick={() => handleActivateUser(u.id)}>Activate</Button>
                          ) : (
                            <Button size="sm" variant="destructive" onClick={() => handleSuspendUser(u.id)}>Suspend</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="coupons" className="admin-content">
          <Card>
            <CardHeader>
              <CardTitle>Coupon Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="create-form">
                <Input placeholder="Coupon Code" value={newCoupon.code} onChange={e => setNewCoupon({...newCoupon, code: e.target.value})} />
                <Input type="number" placeholder="Discount %" value={newCoupon.discount_percent} onChange={e => setNewCoupon({...newCoupon, discount_percent: parseInt(e.target.value)})} />
                <Input type="number" placeholder="Max Uses" value={newCoupon.max_uses} onChange={e => setNewCoupon({...newCoupon, max_uses: parseInt(e.target.value)})} />
                <Button onClick={handleCreateCoupon}>Create Coupon</Button>
              </div>
              <div className="coupons-list">
                {coupons.map(c => (
                  <div key={c.id} className="coupon-item">
                    <div>
                      <strong>{c.code}</strong> - {c.discount_percent}% off - {c.uses}/{c.max_uses} uses
                    </div>
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteCoupon(c.id)}>Delete</Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="affiliates" className="admin-content">
          <Card>
            <CardHeader>
              <CardTitle>Affiliate Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="create-form">
                <Input placeholder="User ID" value={newAffiliate.user_id} onChange={e => setNewAffiliate({...newAffiliate, user_id: e.target.value})} />
                <Input placeholder="PayPal Email" value={newAffiliate.paypal_email} onChange={e => setNewAffiliate({...newAffiliate, paypal_email: e.target.value})} />
                <Button onClick={handleCreateAffiliate}>Create Affiliate</Button>
              </div>
              <div className="affiliates-table">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>User</th>
                      <th>PayPal</th>
                      <th>Referrals</th>
                      <th>Total Earned</th>
                      <th>Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affiliates.map(a => (
                      <tr key={a.id}>
                        <td><strong>{a.affiliate_code}</strong></td>
                        <td>{a.user_email}</td>
                        <td>{a.paypal_email}</td>
                        <td>{a.referrals}</td>
                        <td>${a.total_earned.toFixed(2)}</td>
                        <td>${a.pending_payout.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payouts" className="admin-content">
          <Card>
            <CardHeader>
              <CardTitle>Pending Affiliate Payouts</CardTitle>
              <CardDescription>Total pending: ${pendingPayouts.reduce((sum, p) => sum + p.pending_payout, 0).toFixed(2)}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="payouts-list">
                {pendingPayouts.map(p => (
                  <div key={p.id} className="payout-item">
                    <div className="payout-info">
                      <div><strong>{p.user_name}</strong> ({p.user_email})</div>
                      <div>PayPal: {p.paypal_email}</div>
                      <div>Code: {p.affiliate_code} | Referrals: {p.referrals}</div>
                      <div className="payout-amount">${p.pending_payout.toFixed(2)}</div>
                    </div>
                    <Button onClick={() => handleMarkPaid(p.id, p.pending_payout)}>Mark as Paid</Button>
                  </div>
                ))}
                {pendingPayouts.length === 0 && <p>No pending payouts</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="admin-content">
          <Card>
            <CardHeader>
              <CardTitle>System Settings</CardTitle>
              <CardDescription>Update API keys and system configuration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="settings-form">
                <div className="setting-item">
                  <Label>OpenAI API Key</Label>
                  <Input type="password" value={settings.openai_key} onChange={e => setSettings({...settings, openai_key: e.target.value})} />
                </div>
                <div className="setting-item">
                  <Label>Stripe Secret Key</Label>
                  <Input type="password" value={settings.stripe_secret_key} onChange={e => setSettings({...settings, stripe_secret_key: e.target.value})} />
                </div>
                <div className="setting-item">
                  <Label>Stripe Publishable Key</Label>
                  <Input value={settings.stripe_publishable_key} onChange={e => setSettings({...settings, stripe_publishable_key: e.target.value})} />
                </div>
                <Button onClick={handleUpdateSettings}>Update Settings</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Auth Processor Component
function AuthProcessor() {
  const navigate = useNavigate();
  const location = useLocation();
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const processAuth = async () => {
      const hash = location.hash;
      if (!hash || processing) return;
      
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (sessionId) {
        setProcessing(true);
        try {
          setStatus('Authenticating...');
          console.log('=== AUTH FLOW DEBUG ===');
          console.log('Processing auth with session_id:', sessionId);
          console.log('API endpoint:', `${API}/auth/session`);
          
          const response = await axios.post(`${API}/auth/session`, 
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          console.log('Auth successful:', response.data);
          console.log('Response headers:', response.headers);
          console.log('Cookies after auth:', document.cookie);
          
          setStatus('Setting up your session...');
          
          // Wait longer for cookies to be set - increased to 3 seconds
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          console.log('Cookies after wait:', document.cookie);
          console.log('Redirecting to /dashboard...');
          
          setStatus('Redirecting to dashboard...');
          
          // Clear the hash before redirecting
          window.history.replaceState({}, document.title, '/dashboard');
          
          // Small delay before redirect
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Force page reload to ensure cookies are loaded
          window.location.href = '/dashboard';
        } catch (error) {
          console.error('Auth error:', error);
          console.error('Error response:', error.response?.data);
          toast.error('Authentication failed. Please try again.');
          setStatus('');
          navigate('/', { replace: true });
        } finally {
          setProcessing(false);
        }
      }
    };

    processAuth();
  }, [location.hash]);

  if (processing) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#0f172a',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <Loader2 className="w-12 h-12 animate-spin" style={{color: '#0ea5e9', marginBottom: '1rem'}} />
        <p style={{color: '#fff', fontSize: '1.25rem', fontWeight: '600'}}>{status}</p>
        <p style={{color: '#64748b', fontSize: '0.875rem', marginTop: '0.5rem'}}>Please wait...</p>
      </div>
    );
  }

  return null;
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    // Check if we have a session token in URL hash
    if (location.hash && location.hash.includes('session_id')) {
      return (
        <div className="loading-screen">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p>Signing you in...</p>
        </div>
      );
    }
    return <Navigate to="/" replace />;
  }

  return children;
}

// Main App
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
      </Routes>
      <AuthProcessor />
    </BrowserRouter>
  );
}

export default App;